/**
 * Approvals — report versions waiting for sign-off, in one queue.
 *
 * `GET /approvals` also returns confirmed mappings waiting to be processed;
 * those are intake work, shown only to someone who can do intake work.
 */
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from '../provider/auth';
import { useApprovalsQuery } from '../queries/firm';
import type { ApprovalItem } from '../schemas';
import { ago, count } from '../utils/format';
import { Card, ErrorNotice } from '../shared/ui';
import { DataTable, type Column } from '../features/shared/tables';
import { LoadingState } from '../features/shared/states';
import { PageHeader } from '../provider/pageTitle';

function destination(item: ApprovalItem): string {
	if (item.type === 'REPORT') {
		return item.fkAuditId ? `/audits/${item.fkAuditId}/report` : '/audits';
	}
	return '/clients';
}

const KIND_LABEL: Record<ApprovalItem['type'], string> = {
	REPORT: 'Report',
	MAPPING: 'File to process',
};

export default function Approvals() {
	const navigate = useNavigate();
	const { permissions } = useAuth();
	const { data, error, isPending, refetch } = useApprovalsQuery();

	if (isPending) return <LoadingState label="Loading approvals…" />;
	if (error) return <ErrorNotice error={error} onRetry={refetch} />;

	/* A mapping item is intake work: only someone who can process files sees it. */
	const items = (data?.approvals ?? []).filter(
		(item) => item.type === 'REPORT' || Boolean(permissions?.mapFormats),
	);

	const columns: Array<Column<ApprovalItem>> = [
		{
			key: 'kind',
			label: 'Type',
			width: 140,
			render: (row) => KIND_LABEL[row.type],
		},
		{
			key: 'what',
			label: 'Item',
			width: 320,
			sortValue: (row) => row.title,
			render: (row) => <b>{row.title}</b>,
		},
		{
			key: 'client',
			label: 'Client',
			width: 180,
			sortValue: (row) => row.clientName ?? '',
			render: (row) => row.clientName,
		},
		{
			key: 'waitingOn',
			label: 'Waiting for',
			width: 220,
			render: (row) => (
				<span className="small">
					{row.waitingOn}
					{row.confirmedByName && (
						<span className="mut">
							{' '}
							· confirmed by {row.confirmedByName}
						</span>
					)}
				</span>
			),
		},
		{
			key: 'since',
			label: 'Waiting since',
			width: 140,
			sortValue: (row) => row.since ?? '',
			render: (row) => (row.since ? ago(row.since) : '—'),
		},
	];

	return (
		<Box className="stack">
			<PageHeader
				title="Approvals"
				subtitle={`${count(items.length)} waiting`}
			/>
			<Card>
				<Box className="card-bd flush">
					<DataTable
						rows={items}
						columns={columns}
						rowKey={(row) => `${row.type}-${row.id}`}
						onRowClick={(row) => navigate(destination(row))}
						caption="Approvals"
						empty="Nothing is waiting for approval."
					/>
				</Box>
			</Card>
		</Box>
	);
}
